(() => {
  const drawer = document.querySelector('#halo-cart-sidebar.olitans-cart-drawer');

  if (!drawer) return;

  const body = document.body;
  const mobileQuery = window.matchMedia('(max-width: 767px)');
  const pressSelector = [
    '.previewCartItem-qty .btn-quantity',
    '.previewCartGroup .button',
    '.cartTool-item',
    '.previewCartItem-remove',
    '.halo-sidebar-close'
  ].join(',');

  let pressedElement = null;
  let dragState = null;
  let closeTimer = null;
  let drawerRenderFrame = null;
  let drawerReadyTimer = null;
  let drawerAjaxLoadingSeen = false;
  let drawerWasOpen = body.classList.contains('cart-sidebar-show');
  let cartStatusTimer = null;

  body.classList.add('olitans-cart-drawer-ready');

  const clearDrawerPending = () => {
    window.clearTimeout(drawerReadyTimer);
    drawerReadyTimer = null;
    body.classList.remove('olitans-cart-drawer-pending');
  };

  const markDrawerPending = () => {
    if (body.classList.contains('template-cart')) return;
    if (drawer.querySelector('.previewCart-wrapper .previewCart')) return;

    body.classList.add('olitans-cart-drawer-pending');
    window.clearTimeout(drawerReadyTimer);
    drawerReadyTimer = window.setTimeout(clearDrawerPending, 2200);
  };

  const syncDrawerRenderState = () => {
    drawerRenderFrame = null;

    const isOpen = body.classList.contains('cart-sidebar-show');

    if (!isOpen) {
      if (drawerWasOpen) {
        drawerAjaxLoadingSeen = false;
        clearDrawerPending();
      }

      drawerWasOpen = false;
      return;
    }

    drawerWasOpen = true;

    const cartWrapper = drawer.querySelector('.previewCart-wrapper');
    const isLoading = cartWrapper?.classList.contains('is-loading');
    const hasRenderedCart = Boolean(cartWrapper?.querySelector('.previewCart'));

    if (isLoading && !hasRenderedCart) {
      drawerAjaxLoadingSeen = true;
      markDrawerPending();
      return;
    }

    if (hasRenderedCart && body.classList.contains('olitans-cart-drawer-pending')) {
      drawerAjaxLoadingSeen = false;
      window.clearTimeout(drawerReadyTimer);
      drawerReadyTimer = window.setTimeout(clearDrawerPending, 90);
    }
  };

  const scheduleDrawerRenderSync = () => {
    if (drawerRenderFrame) return;
    drawerRenderFrame = window.requestAnimationFrame(syncDrawerRenderState);
  };

  const isInteractiveTarget = (target) => Boolean(target.closest([
    'a',
    'button',
    'input',
    'textarea',
    'select',
    'summary',
    'label',
    '[role="button"]',
    '.cartTool-item',
    '.previewCartItem-qty',
    '.previewCartItem-remove',
    '.previewCartCollection'
  ].join(',')));

  const isHeaderDragTarget = (target, clientY) => {
    if (target.closest('.olitans-cart-drawer__handle, .halo-sidebar-header')) return true;

    const rect = drawer.getBoundingClientRect();
    return clientY - rect.top <= 82;
  };

  const clearPressed = () => {
    if (!pressedElement) return;
    pressedElement.classList.remove('olitans-cart-drawer-pressed');
    pressedElement = null;
  };

  const addClosingState = () => {
    body.classList.add('olitans-cart-drawer-closing');
    scheduleDrawerRenderSync();
    window.clearTimeout(closeTimer);
    closeTimer = window.setTimeout(() => {
      body.classList.remove('olitans-cart-drawer-closing');
    }, 300);
  };

  const closeDrawer = () => {
    if (!body.classList.contains('cart-sidebar-show')) return;
    addClosingState();
    body.classList.remove('cart-sidebar-show');
  };

  const resetDrag = () => {
    drawer.style.removeProperty('transform');
    drawer.style.removeProperty('opacity');
    body.classList.remove('olitans-cart-drawer-dragging');
    dragState = null;
  };

  const syncFreeShippingMessage = () => {
    const wrapper = drawer.querySelector('.haloCalculatorShipping');
    if (!wrapper) return;

    const message = wrapper.querySelector('.message');
    if (!message) return;

    const text = message.textContent.trim();
    const shippingState = wrapper.dataset.freeShippingState;
    const isUnlocked = shippingState === 'unlocked';
    const setMessage = (value) => {
      if (message.textContent.trim() !== value) {
        message.textContent = value;
      }
    };

    message.classList.toggle('olitans-cart-drawer-unlocked', isUnlocked);
    wrapper.classList.toggle('olitans-cart-drawer-shipping-unlocked', isUnlocked);

    if (isUnlocked) {
      setMessage('✓ Free Shipping Unlocked');
      return;
    }

    if (shippingState !== 'locked') return;

    const currency = text.match(/(?:US\$|\$|€|£|¥)\s?\d[\d,.]*/i);
    if (currency) {
      setMessage(`Spend ${currency[0]} more to unlock free shipping.`);
    }
  };

  const getCartStatusRegion = () => {
    let region = drawer.querySelector('[data-olitans-drawer-cart-status]');

    if (region) return region;

    region = document.createElement('p');
    region.className = 'visually-hidden';
    region.dataset.olitansDrawerCartStatus = '';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    drawer.appendChild(region);
    return region;
  };

  const syncAccessibleCartCount = (cart) => {
    if (!cart || typeof cart.item_count !== 'number') return;

    const itemCount = cart.item_count;
    const itemLabel = itemCount === 1 ? 'item' : 'items';
    const cartLabel = `Cart, ${itemCount} ${itemLabel}`;
    const controls = new Set();

    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = itemCount >= 100 && window.cartStrings?.item_99
        ? window.cartStrings.item_99
        : String(itemCount);

      const countBubble = element.closest('.cart-count-bubble');
      const accessibleCount = countBubble?.querySelector('.visually-hidden');
      if (accessibleCount) accessibleCount.textContent = `${itemCount} ${itemLabel}`;

      const control = element.closest('[data-cart-sidebar], .header__icon--cart, a[href$="/cart"], button');
      if (control) controls.add(control);
    });

    controls.forEach((control) => control.setAttribute('aria-label', cartLabel));

    document.querySelectorAll('[data-cart-text]').forEach((element) => {
      element.textContent = itemCount === 1
        ? (window.cartStrings?.item || 'item')
        : (window.cartStrings?.items || 'items');
    });

    if (!body.classList.contains('cart-sidebar-show')) return;

    const statusRegion = getCartStatusRegion();
    window.clearTimeout(cartStatusTimer);
    statusRegion.textContent = '';
    cartStatusTimer = window.setTimeout(() => {
      statusRegion.textContent = `Cart updated. ${itemCount} ${itemLabel} in cart.`;
    }, 50);
  };

  drawer.addEventListener('pointerdown', (event) => {
    const target = event.target.closest(pressSelector);

    if (target && drawer.contains(target)) {
      clearPressed();
      pressedElement = target;
      pressedElement.classList.add('olitans-cart-drawer-pressed');
    }

    if (!mobileQuery.matches || !body.classList.contains('cart-sidebar-show')) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (isInteractiveTarget(event.target)) return;
    if (!isHeaderDragTarget(event.target, event.clientY)) return;

    dragState = {
      pointerId: event.pointerId,
      startY: event.clientY,
      lastY: event.clientY,
      startTime: performance.now()
    };

    drawer.setPointerCapture?.(event.pointerId);
    body.classList.add('olitans-cart-drawer-dragging');
  });

  drawer.addEventListener('pointermove', (event) => {
    if (!dragState || event.pointerId !== dragState.pointerId) return;

    const deltaY = Math.max(0, event.clientY - dragState.startY);
    dragState.lastY = event.clientY;

    if (deltaY <= 0) {
      drawer.style.removeProperty('transform');
      drawer.style.removeProperty('opacity');
      return;
    }

    const eased = Math.min(deltaY, 180);
    const scale = Math.max(.965, 1 - eased / 4000);
    const opacity = Math.max(.72, 1 - eased / 520);

    drawer.style.setProperty('transform', `translate3d(0, ${eased}px, 0) scale(${scale})`, 'important');
    drawer.style.opacity = String(opacity);
  });

  const finishDrag = (event) => {
    if (!dragState || event.pointerId !== dragState.pointerId) return;

    const deltaY = Math.max(0, dragState.lastY - dragState.startY);
    const elapsed = Math.max(1, performance.now() - dragState.startTime);
    const velocity = deltaY / elapsed;

    drawer.releasePointerCapture?.(event.pointerId);

    if (deltaY > 86 || velocity > .55) {
      resetDrag();
      closeDrawer();
      return;
    }

    resetDrag();
  };

  drawer.addEventListener('pointerup', finishDrag);
  drawer.addEventListener('pointercancel', finishDrag);
  drawer.addEventListener('lostpointercapture', () => {
    if (dragState) resetDrag();
  });

  document.addEventListener('pointerup', clearPressed, true);
  document.addEventListener('pointercancel', clearPressed, true);
  document.addEventListener('scroll', clearPressed, true);

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-cart-sidebar]')) {
      markDrawerPending();
    }
  }, true);

  document.addEventListener('click', (event) => {
    if (!body.classList.contains('cart-sidebar-show')) return;
    if (event.target.closest('[data-close-cart-sidebar]')) {
      addClosingState();
      return;
    }

    if (event.target.classList.contains('background-overlay')) {
      addClosingState();
    }
  }, true);

  document.addEventListener('keyup', (event) => {
    if (event.key === 'Escape') addClosingState();
  }, true);

  document.addEventListener('cart-update', (event) => {
    syncAccessibleCartCount(event.detail);
    if (!body.classList.contains('cart-sidebar-show')) return;

    drawerAjaxLoadingSeen = false;
    window.clearTimeout(drawerReadyTimer);
    drawerReadyTimer = window.setTimeout(clearDrawerPending, 90);
  });

  const observer = new MutationObserver(() => {
    window.requestAnimationFrame(syncFreeShippingMessage);
    scheduleDrawerRenderSync();
  });

  const bodyObserver = new MutationObserver(() => {
    scheduleDrawerRenderSync();
  });

  observer.observe(drawer, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['style', 'class']
  });

  bodyObserver.observe(body, {
    attributes: true,
    attributeFilter: ['class']
  });

  syncFreeShippingMessage();
  syncDrawerRenderState();
})();
